<template>
  <div class="recipe">
    <!-- Recipe Name -->
    <div class="d-flex justify-content-between align-items-start">
      <!-- Recipe Name -->
      <h3 class="mb-3" @click="$emit('show-full', recipe)">
        {{ recipe.name }}
      </h3>
    </div>

    <!-- Image Container -->
    <div class="image-container">
      <!-- Image -->
      <img
        v-if="imageUrl && !imageError"
        :src="imageUrl"
        @click="$emit('show-full', recipe)"
        class="img-fluid"
        @load="onImageLoad"
        @error="onImageError"
      />

      <!-- Image placeholder -->
      <div
        v-else
        class="image-placeholder d-flex justify-content-center align-items-center"
      >
        <div class="spinner-border text-wait" role="status">
          <span class="visually-hidden">Загрузка...</span>
        </div>
      </div>
    </div>

    <!-- Combined Row for Rating, Like, and Imagegen -->
    <div class="d-flex justify-content-between align-items-center mb-2 mt-2">
      <!-- Left: Rating -->
      <RecipeRating
        v-if="recipe && recipe.id && !isImagegenPromptActive"
        :recipe-id="recipe.id"
        class="me-auto"
      />

      <!-- Right: Like and Imagegen controls -->
      <div
        class="d-flex align-items-center"
        :class="{ 'flex-grow-1 ms-2': isImagegenPromptActive }"
      >
        <!-- Imagegen for amin Component -->
        <RecipeImagegen
          v-if="recipe && recipe.id"
          class="me-3"
          :recipe-id="recipe.id"
          :recipe-name="recipe.name"
          @image-regenerated="handleImageSuccessfullyRegenerated"
          @regeneration-error="handleImageRegenerationError"
          @prompt-mode-toggled="isImagegenPromptActive = $event"
        />

        <!-- Interest Button (Bookmark) -->
        <RecipeInterest
          v-if="recipe && recipe.id && !isImagegenPromptActive"
          class="me-2"
          :recipe-id="recipe.id"
        />

        <!-- Selection Button Component -->
        <RecipeSelection
          v-if="recipe && recipe.id && !isImagegenPromptActive"
          :recipe-id="recipe.id"
          class="me-2"
        />

        <!-- Like Button -->
        <RecipeLike
          v-if="recipe && recipe.id && !isImagegenPromptActive"
          class="me-0"
          :recipe-id="recipe.id"
        />

        <!-- Share Button -->
        <button
          v-if="recipe && recipe.id && !isImagegenPromptActive"
          @click="shareRecipe"
          class="btn btn-share"
          title="Поделиться"
        >
          <i class="bi bi-share"></i>
        </button>
      </div>
    </div>

    <!-- Error message for image regeneration, aligned to the right part of the controls -->
    <div
      v-if="imageRegenerationErrorMessage"
      class="text-danger mt-1 small text-end"
    >
      {{ imageRegenerationErrorMessage }}
    </div>

    <!-- Description and details -->
    <div v-if="short">
      <div
        class="d-flex justify-content-between align-items-start"
        :class="{ 'mb-2': recipe.description }"
      >
        <p v-if="!isImagegenPromptActive" class="mb-0 me-2 text-start">
          <strong>Описание:</strong> {{ recipe.description }}
        </p>
        <!-- Like and Imagegen buttons are now in the combined row above -->
        <!-- Error message is also moved above -->
      </div>
      <p class="text-start">
        <strong>Время готовки:</strong> {{ recipe.cookingtimeminutes }} мин
      </p>
      <p class="text-start"><strong>Порции:</strong> {{ recipe.servings }}</p>
      <button
        class="btn btn-outline-success cancel-3"
        @click="$emit('show-full', recipe)"
      >
        Подробнее
      </button>
    </div>

    <!-- Full Recipe Details (conditionally rendered) -->
    <div v-if="!short && !isChatFullscreenActive">
      <!-- Author Name -->
      <p v-if="recipe.author_name" class="text-muted text-start mb-3 mt-3">
        <strong>Автор:</strong>
        <a
          href="#"
          @click.prevent="goToAuthorPage(recipe.author_name)"
          class="author-link"
        >
          {{ " " + recipe.author_name }}</a
        >
      </p>

      <!-- Dish Type and Calorie Info Row -->
      <div class="d-flex justify-content-between align-items-center mt-2 mb-3">
        <p v-if="recipe.dishtype" class="mb-0 text-start">
          <strong>Тип:</strong> {{ recipe.dishtype }}
        </p>
        <!-- This span ensures the calorie link is pushed to the right if dishtype is not present -->
        <span v-else class="flex-grow-1"></span>
        <!-- Calorie Section -->
        <div class="calorie-section me-2">
          <!-- Display calculated calories -->
          <a
            href="#"
            @click.prevent="showCalorieDetails"
            v-if="calorieAnalysis && calorieAnalysis.calories_per_serving > 0"
            class="calorie-link"
          >
            {{ calorieAnalysis.calories_per_serving }} ккал
          </a>

          <!-- "Calculate" button -->
          <a
            href="#"
            @click.prevent="calculateCalories"
            v-if="!calorieAnalysis && !isCalculatingCalories"
            class="calorie-link"
          >
            калорийность
          </a>

          <!-- Loading spinner -->
          <div
            v-if="isCalculatingCalories"
            class="d-flex justify-content-end align-items-center text-muted small"
          >
            <span
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            <span>Расчет...</span>
          </div>
        </div>
      </div>

      <!-- Collapsible Calorie Details Section -->
      <div
        v-if="showCalorieDetailsSection && calorieAnalysis"
        class="calorie-details-container mt-2 mb-3 p-3 border rounded"
      >
        <h5 class="mb-3">Расчет калорийности</h5>
        <pre class="calorie-details-text">{{
          calorieAnalysis.calculation_details
        }}</pre>
      </div>

      <p><strong>Описание:</strong> {{ recipe.description }}</p>
      <p><strong>Время готовки:</strong> {{ recipe.cookingtimeminutes }} мин</p>
      <p><strong>Порции:</strong> {{ recipe.servings }}</p>
      <p><strong>Ингредиенты:</strong></p>
      <ul>
        <li
          v-for="ing in recipe.ingredients"
          :key="ing.name"
          v-show="ing.quantity"
        >
          {{ ing.name }} — {{ ing.quantity }}
        </li>
      </ul>
      <p><strong>Инструкции:</strong></p>
      <ol>
        <li v-for="(step, index) in fixedInstructions(recipe)" :key="index">
          {{ step }}
        </li>
      </ol>
      <p>
        <strong>Категории:</strong> {{ (recipe.categories || []).join(", ") }}
      </p>

      <!-- "I Cooked This" Button (only in full view) -->
      <!-- This button now acts as "Add to Cooking List" and navigates -->
      <button
        v-if="!isImagegenPromptActive"
        class="btn btn-outline-warning"
        @click="addToCookingListAndNavigate"
        title="Приготовить это блюдо"
      >
        <i class="bi bi-cart-plus"></i>
        <span class="ms-2 d-sm-inline">Приготовить</span>
      </button>

      <!-- Make Public Button -->
      <button
        v-if="isOwner && !isPublished"
        class="btn btn-outline-info ms-2"
        @click="makePublic"
        :disabled="isMakingPublic"
        title="Сделать этот рецепт видимым для всех"
      >
        <i class="bi bi-globe"></i>
        <span v-if="!isMakingPublic" class="ms-2 d-sm-inline"
          >Опубликовать</span
        >
        <span v-if="isMakingPublic" class="ms-2 d-sm-inline"
          >Публикация...</span
        >
      </button>

      <!-- Unpublish Button -->
      <button
        v-if="isOwner && isPublished"
        class="btn btn-outline-secondary ms-2"
        @click="makePrivate"
        :disabled="isMakingPrivate"
        title="Снять с публикации и сделать видимым только для вас"
      >
        <i class="bi bi-eye-slash"></i>
        <span v-if="!isMakingPrivate" class="ms-2 d-sm-inline"
          >Снять с публикации</span
        >
        <span v-if="isMakingPrivate" class="ms-2 d-sm-inline">Отменяем...</span>
      </button>

      <div
        v-if="makePublicMessage"
        class="form-text mt-2"
        :class="{
          'text-danger': makePublicError,
          'text-success': !makePublicError,
        }"
      >
        {{ makePublicMessage }}
      </div>
      <div
        v-if="makePrivateMessage"
        class="form-text mt-2"
        :class="{
          'text-danger': makePrivateError,
          'text-success': !makePrivateError,
        }"
      >
        {{ makePrivateMessage }}
      </div>
      <!-- Chat Toggle Button is moved outside this div if chat is active -->
    </div>

    <!-- Recipe Companions Component -->
    <RecipeCompanions
      v-if="recipe && recipe.id && !short"
      class="mt-4"
      :recipe-id="recipe.id"
    />

    <!-- Recipe Q&A Component -->
    <RecipeQA
      v-if="recipe && recipe.id && !short"
      class="mt-4"
      :recipe-id="recipe.id"
    />

    <!-- Chat Component -->
    <!-- The ChatRecipe component is now self-contained. It will show its own toggle button. -->
    <ChatRecipe
      v-if="recipe && recipe.id && !short"
      class="mt-4"
      :recipe-id="recipe.id"
      title="Помощник в готовке"
      @fullscreen-toggled="isChatFullscreenActive = $event"
      ref="chatRecipeComponent"
    />
    <div
      v-if="recipe && recipe.id && !short"
      class="text-secondary small mt-n2 mb-3"
    >
      Инструкции и советы по приготовлению блюда от AI.
    </div>

    <!-- Recipe Articles Section -->
    <RecipeArticles
      v-if="recipe && recipe.id && !short"
      :recipe-id="recipe.id"
    />

    <!-- Similar Recipes Section -->
    <SimilarRecipes
      v-if="recipe && recipe.id && !short"
      :recipe-id="recipe.id"
    />
  </div>
</template>

<script>
import RecipeLike from "./RecipeLike.vue";
import RecipeRating from "./RecipeRating.vue";
import RecipeInterest from "./RecipeInterest.vue";
import RecipeSelection from "./RecipeSelection.vue";
import RecipeImagegen from "./RecipeImagegen.vue";
import ChatRecipe from "./ChatRecipe.vue";
import SimilarRecipes from "./SimilarRecipes.vue";
import RecipeQA from "./RecipeQA.vue";
import RecipeCompanions from "./RecipeCompanions.vue";
import RecipeArticles from "./RecipeArticles.vue";
import { logEvent } from "@/services/analytics.js";
export default {
  name: "RecipeView",
  props: {
    recipe: Object,
    short: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    RecipeLike,
    RecipeRating,
    RecipeInterest,
    RecipeSelection,
    RecipeImagegen,
    ChatRecipe,
    SimilarRecipes,
    RecipeQA,
    RecipeCompanions,
    RecipeArticles,
  },
  data() {
    return {
      imageUrl: null,
      imageError: false,
      imageLoaded: false,
      imageRegenerationErrorMessage: null,
      isImagegenPromptActive: false, // New state to track prompt mode
      isChatFullscreenActive: false, // Tracks if the chat child component is fullscreen
      isMarkingAsCooked: false,
      hasBeenCooked: false, // To provide feedback after clicking
      showCalorieDetailsSection: false,
      isCalculatingCalories: false,
      calorieAnalysis: null,
      isOwner: false,
      isPublished: false,
      isAdmin: false,
      makePublicMessage: "",
      makePublicError: false,
      isMakingPublic: false,
      isMakingPrivate: false,
      makePrivateMessage: "",
      makePrivateError: false,
    };
  },

  watch: {
    recipe: {
      immediate: true,
      handler(newRecipe) {
        this.isImagegenPromptActive = false; // Reset when recipe changes
        this.isChatFullscreenActive = false; // Reset chat fullscreen state
        this.showCalorieDetailsSection = false; // Hide details on recipe change
        this.calorieAnalysis = newRecipe.calorie_analysis || null;
        this.imageRegenerationErrorMessage = null; // Clear error on recipe change
        this.isOwner = newRecipe.is_owner || false;
        this.isPublished = newRecipe.show || false;
        this.isAdmin = newRecipe.is_admin || false;
        this.fetchRecipeImage(newRecipe);

        // Log analytics event for full recipe view
        if (newRecipe && newRecipe.id && !this.short) {
          if (this.$globalState.analytics) {
            logEvent(this.$globalState.analytics, "view_item", {
              item_id: newRecipe.id,
              item_name: newRecipe.name,
              item_category: newRecipe.dishtype,
            });
            console.log(
              `Analytics: Logged view_item for recipe: ${newRecipe.name} (ID: ${newRecipe.id})`
            );
          }
        }
      },
    },
    "$globalState.apiUrl"(newApiUrl) {
      // Watch for changes in the global API URL
      if (newApiUrl && this.recipe && this.recipe.id) {
        // If API URL becomes available and we have a recipe, check statuses
        console.log(
          "RecipeView: Global API URL available/changed, re-checking state."
        );
        if (!this.imageUrl || this.imageError) {
          this.fetchRecipeImage(this.recipe);
        }
      }
    },
  },

  methods: {
    goToAuthorPage(authorName) {
      if (!authorName) return;
      window.location.hash = `/author/${encodeURIComponent(authorName)}`;
    },

    async makePrivate() {
      if (!this.isOwner || !this.isPublished) {
        return;
      }

      this.isMakingPrivate = true;
      this.makePrivateMessage = "";
      this.makePrivateError = false;
      try {
        await this.sendCmd(`recipe.makePrivate/${this.recipe.id}`);
        this.makePrivateMessage = "Рецепт снят с публикации.";
        this.makePrivateError = false;
        this.isPublished = false; // Hide the button and show the "Publish" one
      } catch (error) {
        this.makePrivateMessage =
          error.message || "Не удалось снять рецепт с публикации.";
        this.makePrivateError = true;
        console.error("Failed to make recipe private:", error);
      } finally {
        this.isMakingPrivate = false;
      }
    },

    async makePublic() {
      if (!this.isOwner || this.isPublished) {
        return;
      }

      this.isMakingPublic = true;
      this.makePublicMessage = "";
      this.makePublicError = false;
      try {
        await this.sendCmd(`recipe.makePublic/${this.recipe.id}`);
        this.makePublicMessage = "Рецепт успешно опубликован!";
        this.makePublicError = false;
        this.isPublished = true; // Hide the button after success
      } catch (error) {
        this.makePublicMessage =
          error.message || "Не удалось опубликовать рецепт.";
        this.makePublicError = true;
        console.error("Failed to make recipe public:", error);
      } finally {
        this.isMakingPublic = false;
      }
    },

    onImageLoad() {
      this.imageLoaded = true;
      this.imageError = false; // Reset error on successful load
    },
    onImageError() {
      // If the first attempt fails, try again with a cache-busting parameter.
      if (this.imageUrl && !this.imageUrl.includes("?cb=")) {
        this.imageUrl = `${this.imageUrl}?cb=1`; // ${new Date().getTime()}`;
      } else {
        // If it fails even with the cache buster, then it's a real error.
        this.imageError = true;
      }
    },

    fixedInstructions(recipe) {
      if (!recipe || !recipe.instructions) {
        return [];
      }

      let instructionsString = recipe.instructions;
      if (Array.isArray(recipe.instructions)) {
        // If instructions is an array, join it into a single string.
        // The backend (recipes.ToSQL) uses "\\n" when creating the SQL.
        // When AI generates it as []string, we join with "\n" for consistency.
        instructionsString = recipe.instructions.join("\n");
      }

      return instructionsString
        .replace(/\\\\n/g, "\n") // Handle escaped newlines if present
        .replace(/\\n/g, "\n") // Handle regular newlines
        .split("\n") // Split into an array of lines
        .map((line) => line.replace(/^\d+\.\s*/, "").trim()) // Remove numbering and trim
        .filter((line) => line.length > 0); // Remove empty lines
    },
    fetchRecipeImage(currentRecipe) {
      if (!currentRecipe || !currentRecipe.name) {
        this.imageUrl = null;
        this.imageLoaded = false;
        this.imageError = false;
        this.isImagegenPromptActive = false;
        this.isChatFullscreenActive = false;
        this.showChat = false;
        this.imageRegenerationErrorMessage = null;
        return;
      }
      // For now, RecipeLike handles its own state.
      if (currentRecipe.id !== (this.recipe ? this.recipe.id : null)) {
        // This block was empty, causing ESLint error.
        // TODO: If recipe changed and specific logic is needed here (beyond what RecipeLike handles),
        // implement it. For now, it's kept empty or can be removed if truly not needed.
      }

      this.imageUrl = null;
      this.imageLoaded = false;
      this.imageError = false;
      this.isImagegenPromptActive = false; // Reset on new image fetch
      this.isChatFullscreenActive = false; // Reset chat fullscreen state
      this.imageRegenerationErrorMessage = null; // Clear previous errors

      const baseUrl = this.$globalState.apiUrl;

      if (baseUrl === null) {
        console.warn(
          "RecipeView: API URL not yet available. Image will not load yet."
        );
        return; // Exit if URL is not ready
      }
      if (!baseUrl) {
        console.error(
          "RecipeView: Base API URL is empty. Image cannot be loaded."
        );
        this.imageError = true;
        return;
      }

      const encodedRecipeName = encodeURIComponent(currentRecipe.name);
      this.imageUrl = `${baseUrl}/image.get/${encodedRecipeName}`;
      console.log("RecipeView: Image URL set to:", this.imageUrl);
    },

    handleLikeToggledInView(newIsLiked, recipeId) {
      console.log(
        `RecipeView: Like status for recipe ${recipeId} changed to ${newIsLiked}`
      );
    },
    handleImageSuccessfullyRegenerated() {
      if (!this.recipe || !this.recipe.name || !this.$globalState.apiUrl) {
        console.warn(
          "RecipeView: Cannot refresh image, missing recipe data or API URL."
        );
        return;
      }
      this.imageRegenerationErrorMessage = null; // Clear any previous error message
      console.log(
        "RecipeView: Image successfully regenerated by child component, forcing reload."
      );

      const baseUrl = this.$globalState.apiUrl;
      const encodedRecipeName = encodeURIComponent(this.recipe.name);
      const cacheBuster = `?cb=${new Date().getTime()}`;

      // Reset image state to ensure UI updates (e.g., shows spinner)
      this.imageLoaded = false;
      this.imageError = false;
      this.imageUrl = null; // Temporarily nullify to ensure Vue detects the change

      // Use $nextTick to ensure the DOM has updated after nullifying imageUrl
      // before setting the new URL. This helps in reliably triggering a re-render.
      this.$nextTick(() => {
        this.imageUrl = `${baseUrl}/image.get/${encodedRecipeName}${cacheBuster}`;
        console.log(
          "RecipeView: New image URL with cache buster:",
          this.imageUrl
        );
      });
    },
    handleImageRegenerationError(error, recipeId) {
      console.error(
        `RecipeView: Error regenerating image for recipe ID ${recipeId}:`,
        error
      );
      const recipeName =
        this.recipe && this.recipe.name
          ? `"${this.recipe.name}"`
          : `(ID: ${recipeId})`;
      this.imageRegenerationErrorMessage = `Ошибка регенерации изображения для ${recipeName}. Попробуйте позже.`;
      // Ensure other image state is consistent if needed
    },

    addToCookingListAndNavigate() {
      if (!this.recipe || !this.recipe.id) return;

      const recipeId = Number(this.recipe.id);
      if (!this.$globalState.selectedRecipes.includes(recipeId)) {
        this.$globalState.selectedRecipes.push(recipeId);
      }

      // Navigate to the cooking list page
      window.location.hash = "/cooking-list";
    },

    async shareRecipe() {
      if (!this.recipe) return;

      // This URL will be handled by our new backend endpoint
      const shareUrl = `/recipe/${this.recipe.id}`;
      const shareData = {
        title: this.recipe.name,
        text: this.recipe.description,
        url: shareUrl,
      };

      if (navigator.share) {
        try {
          await navigator.share(shareData);
        } catch (err) {
          console.error("Error sharing:", err);
        }
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(shareUrl);
        // TODO: Replace alert with a more elegant notification component later
        alert("Ссылка на рецепт скопирована!");
      }
    },

    showCalorieDetails() {
      // Just toggle the visibility of the details section
      this.showCalorieDetailsSection = !this.showCalorieDetailsSection;
    },

    async calculateCalories() {
      if (!this.recipe || !this.recipe.id || this.isCalculatingCalories) {
        return;
      }
      this.isCalculatingCalories = true;
      try {
        const command = `recipe.calculate_calories/${this.recipe.id}`;
        const responseRaw = await this.sendCmd(command, null, 30000); // 30s timeout
        const newAnalysis = JSON.parse(responseRaw);

        // Update local data property instead of mutating a prop
        this.calorieAnalysis = newAnalysis;
      } catch (error) {
        console.error("Error calculating calories:", error);
        alert("Не удалось рассчитать калорийность. Попробуйте позже.");
      } finally {
        this.isCalculatingCalories = false;
      }
    },
  },
};
</script>

<style scoped>
.recipe ul,
.recipe ol {
  padding-left: 1.4rem;
}

.image-container {
  border-radius: 8px;
  overflow: hidden;
}

img {
  transition: transform 0.3s ease-in-out;
}

img:hover {
  transform: scale(1.03);
}

.image-placeholder {
  min-height: calc(100vw * 0.5);
  max-height: 250px;
}

.btn-like {
  background: none;
  border: none;
  font-size: 1.5rem;
  padding: 0;
}

.btn-share {
  font-size: 1.5rem;
  margin-bottom: 0.61rem;
}

.calorie-link {
  text-decoration: none;
  color: var(--bs-success);
  font-weight: 500;
}

.calorie-section {
  min-width: 180px; /* Reserve space to prevent layout shift */
  text-align: right;
}

.calorie-details-container {
  background-color: var(--bs-tertiary-bg);
  border-color: var(--bs-border-color-translucent) !important;
}

.calorie-details-text {
  white-space: pre-wrap; /* Ensures long lines wrap */
  word-wrap: break-word;
}

.author-link {
  text-decoration: none;
  color: var(--bs-body-color);
}
</style>
